import { useMemo, useSyncExternalStore } from 'react';

export interface Remaining {
  hh: number;
  mm: number;
  ss: number;
  expired: boolean;
}

export function computeRemaining(targetMs: number | null, nowMs: number): Remaining {
  if (targetMs == null) return { hh: 0, mm: 0, ss: 0, expired: true };
  const diff = targetMs - nowMs;
  if (diff <= 0) return { hh: 0, mm: 0, ss: 0, expired: true };
  const total = Math.floor(diff / 1000);
  const hh = Math.floor(total / 3600);
  const mm = Math.floor((total % 3600) / 60);
  const ss = total % 60;
  return { hh, mm, ss, expired: false };
}

/**
 * Seconds left, bucketed to the coarsest resolution the UI can show: -1 once
 * expired/absent, whole minutes while an hour+ away (cards render "X ชม. Y น."
 * — no seconds), exact seconds inside the last hour. Snapshot equality is what
 * lets React skip re-rendering a 120-card grid on every tick.
 */
export function bucketRemaining(targetMs: number | null, nowMs: number): number {
  if (targetMs == null) return -1;
  const total = Math.floor((targetMs - nowMs) / 1000);
  if (total <= 0) return -1;
  return total >= 3600 ? Math.floor(total / 60) * 60 : total;
}

// One ticker shared by every countdown on the page (vs. an interval per card).
const listeners = new Set<() => void>();
let ticker: ReturnType<typeof setInterval> | null = null;

function subscribe(onTick: () => void): () => void {
  listeners.add(onTick);
  ticker ??= setInterval(() => {
    for (const l of listeners) l();
  }, 1000);
  return () => {
    listeners.delete(onTick);
    if (listeners.size === 0 && ticker) {
      clearInterval(ticker);
      ticker = null;
    }
  };
}

export function useCountdown(targetIso: string | null | undefined): Remaining {
  const targetMs = useMemo(() => (targetIso ? new Date(targetIso).getTime() : null), [targetIso]);
  const bucket = useSyncExternalStore(subscribe, () => bucketRemaining(targetMs, Date.now()));
  return useMemo<Remaining>(() => {
    if (bucket < 0) return { hh: 0, mm: 0, ss: 0, expired: true };
    return {
      hh: Math.floor(bucket / 3600),
      mm: Math.floor((bucket % 3600) / 60),
      ss: bucket % 60,
      expired: false,
    };
  }, [bucket]);
}
